import * as React from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import {
  IPingDTO,
  IDriverSearchObject,
  IMarker,
  IMap,
  IProviderProductObject,
  IServiceObject,
  ICoordinates,
  ITripFilters
} from '../../types'
import { mapViewStrategyStages } from '../MapView'
import ErrorBoundary from '../ErrorBoundary'
import PreloaderCircle from '../ui/PreloaderCircle'
import moment from 'moment'

const LiveDriver = (props: {
  operator: IDriverSearchObject
  ping?: IPingDTO
  products: IMap<IProviderProductObject>
  locateDriver: (coordinates: ICoordinates) => void
  autoRefresh: boolean
}) => {
  const { operator, ping, products, autoRefresh } = props
  const tripId = ping ? ping.tripId : undefined
  const dispatchedTripId = ping ? ping.dispatchedTripId : null
  const vehicle = ''
  const locateDriver = () => {
    if (ping) {
      props.locateDriver({ lat: ping.location.lat, lng: ping.location.lng })
    }
  }

  const hasBeenUpdated = (date: string) => {
    return !moment(date)
      .add(40, 'seconds')
      .isBefore(new Date())
  }
  const getColorCode = (ping: IPingDTO | undefined) => {
    if (ping) {
      if (ping.trips && ping.trips.length > 0 && (autoRefresh === true ? hasBeenUpdated(ping.updatedAt) : true)) {
        return 'bg-blue'
      }
      if ((!ping.trips || ping.trips.length === 0) && (autoRefresh === true ? hasBeenUpdated(ping.updatedAt) : true)) {
        return 'bg-green'
      }
    }
    return 'bg-red'
  }

  const customizedTimeAgo = (checkInTime: Date | undefined, updatedAt: string) => {
    moment.defineLocale('en-custom', {
      relativeTime: {
        future: '%s',
        past: '%s',
        s: '1 second',
        ss: '%ds',
        m: '1m',
        mm: '%dm',
        h: '1hr',
        hh: '%dhr',
        d: '1d',
        dd: '%dd',
        M: '1mo',
        MM: '%dmo',
        y: '1y',
        yy: '%dy'
      }
    })

    let label = ''
    if (checkInTime && hasBeenUpdated(updatedAt)) {
      label = moment(checkInTime)
        .locale('en-custom')
        .from(updatedAt)
    }
    if (!hasBeenUpdated(updatedAt)) {
      label = moment(updatedAt)
        .locale('en-custom')
        .fromNow()
    }
    if (label.includes('second')) {
      return 'just now'
    }
    return label
  }
  const OnlineIndicatorStatus = (prop: { ping: IPingDTO | undefined }) => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: '0.55rem',
          minWidth: '55px'
        }}
      >
        <p
          className={classNames(`circle mt0 mb0 ${getColorCode(prop.ping)}`)}
          style={{ width: '15px', height: '15px' }}
        />
        {prop.ping && prop.ping.updatedAt && (
          <span className='h6 bold font-gray5'>{customizedTimeAgo(prop.ping.checkInTime, prop.ping.updatedAt)}</span>
        )}
      </div>
    )
  }

  return (
    <div className={classNames('hand flex items-center p1', { 'bg-green': dispatchedTripId })}>
      <div onClick={locateDriver}>
        <div className='flex items-center'>
          <div className='flex flex-column'>
            <OnlineIndicatorStatus ping={ping} />
            {tripId ? (
              <div className='font-small font-blue text-center'>
                <Link to={`/trips/${tripId}`}>Active</Link>
              </div>
            ) : null}
            {dispatchedTripId ? (
              <div className='font-small font-blue text-center'>
                <Link to={`/trips/${dispatchedTripId}`}>Receiving dispatch!!</Link>
              </div>
            ) : null}
          </div>

          {/* Live Drivers Pane - Avatars for the driver on side panel */}
          {/* <ImageLiveDrivers name={operator.name} entityId={operator.id} width='30px' height='30px' isRounded={true} /> */}
          {/* Live Drivers Pane - Avatars for the driver on side panel */}
          <div className='flex flex-column justify-center'>
            <div className='ml1 mt0 mb0 col-6 font-black bold h5 w100 pb1'>{operator.name} </div>
            <div>
              {operator.vehicle && (
                <div className='ml1 font-grey bold h5 w100'>
                  {operator.vehicle.model} {operator.vehicle.licensePlate}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className=''>
          <p className='ml1 mt0 mb0 font-small'>{vehicle}</p>
        </div>
      </div>
    </div>
  )
}

export interface ILiveDriversPaneProps {
  operators: IDriverSearchObject[]
  products: IMap<IProviderProductObject>
  operatorPings: IPingDTO[]
  currentOrganizationId: string
  currentServiceId: string
  currentService: IServiceObject
  hidden: boolean
  tripFilters: ITripFilters
  getOrgOperators: (orgId: string) => Promise<any>
  getOperators: (organizationId: string, serviceIds?: string[]) => Promise<any>
  getService: (serviceId: string) => Promise<any>
  addDriversToMap: (driverLocations: IMarker[]) => Promise<any>
  setCurrentTripStage: (stage: string) => Promise<any>
  clearMap: () => void
  locateDriver: (coordinates: ICoordinates) => void
}

export interface ILiveDriverPaneState {
  autoRefresh: boolean
  isFetching: boolean
  autoRefreshTurnOnTime: Date | null
}

const INTERVAL = 20000
const AUTO_DISABLE_INTERVAL = 300000 // auto disables after 5 minutes of being live
const WILS_ORG_ID = '2997eee3-2f81-4064-a508-a207d5b12b22' // Org ID for WILS in order to set autoRefresh by default to true

class LiveDriversPane extends React.Component<ILiveDriversPaneProps, ILiveDriverPaneState> {
  private pingTimerId: any

  constructor(props: ILiveDriversPaneProps) {
    super(props)
    this.state = {
      autoRefresh: false,
      isFetching: true,
      autoRefreshTurnOnTime: null
    }
  }
  public async componentDidMount() {
    const { currentOrganizationId, tripFilters } = this.props
    if (currentOrganizationId) {
      if (currentOrganizationId === WILS_ORG_ID) {
        this.setState({
          autoRefresh: true,
          autoRefreshTurnOnTime: moment().toDate()
        })
      }
      try {
        await this.props.getOrgOperators(currentOrganizationId)
        await this.props.getOperators(currentOrganizationId, tripFilters.serviceIds)
        await this.props.setCurrentTripStage(mapViewStrategyStages.showNearbyDrivers)
      } catch (error) {
        console.error('Failed to initialize WILS organization:', error)
      }
    }

    this.setState({ isFetching: false })

    // should always be updated for timeAgo accuracy.
    const { autoRefresh } = this.state
    if (autoRefresh === true) {
      clearInterval(this.pingTimerId)
      this.pingTimerId = setInterval(this.refreshPings, INTERVAL)
    } else if (autoRefresh === false) {
      clearInterval(this.pingTimerId)
    }
  }

  public componentWillUnmount() {
    clearInterval(this.pingTimerId)
    this.props.clearMap()
  }

  public componentDidUpdate(prevProps: ILiveDriversPaneProps, prevState: ILiveDriverPaneState) {
    const {
      currentOrganizationId: prevOrg,
      operatorPings: prevPings,
      currentServiceId: prevServiceId,
      operators: prevOperators,
      tripFilters: prevTripFilters
    } = prevProps
    const {
      currentOrganizationId: nextOrg,
      operatorPings: nextPings,
      currentServiceId: nextserviceId,
      operators: nextOperators,
      tripFilters
    } = this.props
    const { autoRefresh: prevAutoRefresh } = prevState
    const { autoRefresh: nextAutoRefresh } = this.state
    if (prevOrg !== nextOrg) {
      if (nextOrg === WILS_ORG_ID) {
        this.setState({
          autoRefresh: true,
          autoRefreshTurnOnTime: moment().toDate()
        })
      }
      this.props.getOrgOperators(nextOrg)
      this.props.getOperators(nextOrg)
    }

    // Fetch operators when serviceIds change(filter change)
    if (prevTripFilters.serviceIds !== tripFilters.serviceIds) {
      this.props.getOperators(nextOrg, tripFilters.serviceIds)
    }

    if (
      JSON.stringify(prevPings) !== JSON.stringify(nextPings) ||
      JSON.stringify(prevOperators) !== JSON.stringify(nextOperators) ||
      prevAutoRefresh !== nextAutoRefresh
    ) {
      if (this.state.autoRefresh === true) {
        const operators = this.combineDriverData()
        const markers = operators
          .map(({ operator, ping }) => {
            if (ping) {
              return { type: 'car', lat: ping.location.lat, lng: ping.location.lng, title: operator.name }
            }
            return null
          })
          .filter(result => !!result)
        this.props.addDriversToMap(markers as IMarker[])
        clearInterval(this.pingTimerId)
        this.pingTimerId = setInterval(this.refreshPings, INTERVAL)
        if (moment().diff(moment(this.state.autoRefreshTurnOnTime), 'minutes') >= 5) {
          this.setState({
            autoRefresh: false
          })
        }
      } else {
        this.props.clearMap()
        clearInterval(this.pingTimerId)
      }
    }
    if (nextserviceId && nextserviceId !== prevServiceId) {
      this.props.getService(nextserviceId)
    }
  }

  public render() {
    const { autoRefresh } = this.state
    return (
      <ErrorBoundary>
        <section
          className='shadow-right bg-white overflow-x-hidden overflow-y-scroll'
          style={{ width: '25em', display: this.props.hidden ? 'none' : 'block' }}
        >
          <div className='pt1'>
            <div className='flex items-center justify-center pl2 cursor--pointer pb1'>
              <p
                onClick={this.autoRefresh}
                className={autoRefresh ? 'font-green mt0 mb0 hand h5' : 'font-red mt0 mb0 hand h5'}
              >
                {autoRefresh ? 'Live' : 'Not Live'}&nbsp;
              </p>{' '}
              |<p className='mt0 mb0 h5'>&nbsp;Members</p>
            </div>
          </div>
          <div className='pl1'>{this.renderDrivers()}</div>
        </section>
      </ErrorBoundary>
    )
  }

  private refreshPings = () => {
    const { currentOrganizationId, tripFilters } = this.props
    if (currentOrganizationId) {
      this.props.getOrgOperators(currentOrganizationId)
      this.props.getOperators(this.props.currentOrganizationId, tripFilters.serviceIds)
    }
  }

  private combineDriverData = (): Array<{ operator: IDriverSearchObject; ping?: IPingDTO }> => {
    const { operators, operatorPings, currentService } = this.props

    const serviceProductIds =
      currentService && currentService.providerProducts ? currentService.providerProducts.map(p => p.id) : []

    return operators
      .filter(operator => {
        if (!currentService) {
          return true
        }
        if (operator && operator.vehicle && operator.vehicle.products) {
          return (
            operator.vehicle.products.filter(vehicleProduct => {
              if (currentService && currentService.providerProducts) {
                return !!currentService.providerProducts.find(serviceProduct => {
                  return vehicleProduct.id === serviceProduct.id
                })
              }
              return false
            }).length > 0
          )
        }
        return false
      })
      .map(operator => {
        const ping = operatorPings.find(ping => ping.userId === operator.id)
        return { operator, ping }
      })
  }

  private autoRefresh = () => {
    if (this.state.autoRefresh === false) {
      this.setState({
        autoRefresh: !this.state.autoRefresh,
        autoRefreshTurnOnTime: moment().toDate()
      })
    } else {
      this.setState({
        autoRefresh: !this.state.autoRefresh
      })
    }
    this.refreshPings()
  }
  private renderDrivers = () => {
    const drivers = this.combineDriverData()
    const { products } = this.props
    const sorter = (a: any, b: any) =>
      (!!b.ping ? (!!b.ping.tripId ? 2 : 1) : 0) - (!!a.ping ? (!!a.ping.tripId ? 2 : 1) : 0)
    const styles = {
      CirclePreLoader: {
        display: 'flex',
        paddingTop: '5px',
        flex: 1,
        justifyContent: 'center',
        alignment: 'center',
        width: '100%'
      }
    }

    if (this.state.isFetching) {
      return (
        <div style={styles.CirclePreLoader}>
          <PreloaderCircle loading={true} />
        </div>
      )
    }

    return drivers.sort(sorter).map(d => {
      return (
        <LiveDriver
          products={products}
          operator={d.operator}
          ping={d.ping}
          key={'live-driver-' + d.operator.id}
          locateDriver={this.props.locateDriver}
          autoRefresh={this.state.autoRefresh}
        />
      )
    })
  }
}

export default LiveDriversPane



// WEBPACK FOOTER //
// ./src/components/drivers/LiveDriversPane.tsx